// frontend/src/pages/admin/Contracts.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Input, Popconfirm, Select, Badge, Modal } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import './users.css'; // Assuming this CSS is shared/appropriate - consider renaming
import ContractForm from './ContractForm';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;
const { Option } = Select;

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [softDeletingId, setSoftDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const { appSettings } = useAppContext();
  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const fetchContracts = useCallback(async (
    statusParam = filterStatus,
    searchParam = searchText,
    showSuccessNotification = true
  ) => {
    setLoading(true);
    const loadingKey = 'fetchContractsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading contracts...');
    try {
      const params = {
        status: statusParam,
        search: searchParam,
      };

      const response = await api.get('/api/contracts', { params });
      let fetchedContracts = response.data?.data || [];

      if (!Array.isArray(fetchedContracts)) {
        console.warn('Contracts.js: API response for contracts was not an array. Assuming empty array.');
        fetchedContracts = [];
      }
      setContracts(fetchedContracts);
      hideLoading();
      if (showSuccessNotification) {
        showSuccess('Contracts Loaded', 'Contract list fetched successfully!', 'fetchContractsSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching contracts:', error.response?.data || error.message, error);
      showError('Failed to Fetch Contracts', error.response?.data?.message || 'An unexpected error occurred while fetching contracts.', 'fetchContractsError');
    } finally {
      setLoading(false);
    }
  }, [showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchContracts(filterStatus, searchText);
  }, [fetchContracts, filterStatus, searchText, dateFormat, timeFormat]);

  const handleAddContract = () => {
    setEditingContract(null);
    setIsModalVisible(true);
  };

  const handleEditContract = (contractRecord) => {
    setEditingContract(contractRecord);
    setIsModalVisible(true);
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    fetchContracts(filterStatus, searchText, true);
  };

  const handleDeactivateContract = async (contractId, contractName) => {
    setDeactivatingId(contractId);
    const loadingKey = `contractStatusUpdate-${contractId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating "${contractName}"...`);
    try {
      await api.patch(`/api/contracts/${contractId}/deactivate`);
      hideLoading();
      showSuccess('Contract Status Updated', `"${contractName}" was deactivated.`, `contractStatusUpdateSuccess-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Deactivation error:', error);

      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to deactivate contract "${contractName}"`;
      const ticketCount = apiError?.data?.activeTicketsCount;

      const fullMessage = ticketCount !== undefined && ticketCount > 0
        ? `${rawMessage} (${ticketCount} active ticket${ticketCount > 1 ? 's' : ''})`
        : rawMessage;

      showError('Cannot Deactivate Contract', fullMessage, `deactivateError-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return false;
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateContract = async (contractId, contractName) => {
    setActivatingId(contractId);
    const loadingKey = `contractStatusUpdate-${contractId}`;
    const hideLoading = showLoading(loadingKey, `Activating "${contractName}"...`);
    try {
      await api.patch(`/api/contracts/${contractId}/activate`);
      hideLoading();
      showSuccess('Contract Status Updated', `"${contractName}" activated successfully!`, `contractStatusUpdateSuccess-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Error activating contract:', error);
      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to activate contract "${contractName}".`;

      showError('Cannot Activate Contract', rawMessage, `activateError-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return false;
    } finally {
        setActivatingId(null);
    }
  };

  const handleSoftDeleteContract = async (contractId, contractName) => {
    setSoftDeletingId(contractId);
    const loadingKey = `contractSoftDelete-${contractId}`;
    const hideLoading = showLoading(loadingKey, `Soft deleting "${contractName}"...`);
    try {
      await api.patch(`/api/contracts/${contractId}/soft-delete`);
      hideLoading();
      showSuccess('Contract Soft Deleted', `"${contractName}" soft-deleted successfully!`, `contractSoftDeleteSuccess-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Error soft-deleting contract:', error);
      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to soft-delete contract "${contractName}".`;

      showError('Cannot Soft Delete Contract', rawMessage, `softDeleteError-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return false;
    } finally {
        setSoftDeletingId(null);
    }
  };

  const handleRestoreContract = async (contractId, contractName) => {
    setRestoringId(contractId);
    const loadingKey = `contractRestore-${contractId}`;
    const hideLoading = showLoading(loadingKey, `Restoring "${contractName}"...`);
    try {
      await api.patch(`/api/contracts/${contractId}/restore`);
      hideLoading();
      showSuccess('Contract Restored', `"${contractName}" restored successfully!`, `contractRestoreSuccess-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Error restoring contract:', error);
      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to restore contract "${contractName}".`;

      showError('Cannot Restore Contract', rawMessage, `restoreError-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return false;
    } finally {
        setRestoringId(null);
    }
  };

  const handleHardDeleteContract = async (contractId, contractName) => {
    setHardDeletingId(contractId);
    const loadingKey = `contractHardDelete-${contractId}`;
    const hideLoading = showLoading(loadingKey, `Permanently deleting "${contractName}"...`);
    try {
      await api.delete(`/api/contracts/${contractId}`);
      hideLoading();
      showSuccess('Contract Permanently Deleted', `"${contractName}" permanently deleted!`, `contractHardDeleteSuccess-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Error hard deleting contract:', error);
      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to hard delete contract "${contractName}".`;

      showError('Cannot Permanently Delete Contract', rawMessage, `hardDeleteError-${contractId}`);
      fetchContracts(filterStatus, searchText, false);
      return false;
    } finally {
        setHardDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Contract Name',
      dataIndex: 'contractName',
      key: 'contractName',
      sorter: (a, b) => (a.contractName || '').localeCompare(b.contractName || ''),
      width: '12%',
      render: (text, record) => (
        <a onClick={() => handleEditContract(record)}>{text}</a>
      ),
    },
    {
      title: 'Customer Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
      sorter: (a, b) => (a.organizationName || '').localeCompare(b.organizationName || ''),
      width: '12%',
    },
    {
      title: 'Main Contractor',
      key: 'mainContractorName',
      width: '12%',
      render: (_, record) => {
        // If mainContractorOrganizationId is 1 or YPS, show YPS
        // Otherwise, we need to fetch the name from the organization
        // Since the API might not return the name, we'll check the record
        if (record.mainContractorOrganizationId === 1) {
          return 'YongPeng Solutions Sdn. Bhd. (YPS)';
        }
        // If the API returns mainContractorName, use it
        if (record.mainContractorName) {
          return `${record.mainContractorName} (Partner)`;
        }
        // Fallback: show ID only
        return record.mainContractorOrganizationId ? `Contractor ID: ${record.mainContractorOrganizationId}` : 'Not Set';
      },
      sorter: (a, b) => (a.mainContractorOrganizationId || 0) - (b.mainContractorOrganizationId || 0),
    },
    {
      title: 'Report Title',
      dataIndex: 'reportTitle',
      key: 'reportTitle',
      sorter: (a, b) => (a.reportTitle || '').localeCompare(b.reportTitle || ''),
      width: '20%',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => date ? dayjs(date).format(dateFormat) : 'N/A',
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
      width: '8%',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => date ? dayjs(date).format(dateFormat) : 'N/A',
      sorter: (a, b) => dayjs(a.endDate).valueOf() - dayjs(b.endDate).valueOf(),
      width: '8%',
    },
    {
      title: 'LAD Penalty Rate',
      dataIndex: 'ladPenaltyRate',
      key: 'ladPenaltyRate',
      sorter: (a, b) => (a.ladPenaltyRate || 0) - (b.ladPenaltyRate || 0),
      width: '6%',
    },
    {
      title: 'Maintenance Period',
      dataIndex: 'maintenancePeriodUnit',
      key: 'maintenancePeriodUnit',
      sorter: (a, b) => (a.maintenancePeriodUnit || '').localeCompare(b.maintenancePeriodUnit || ''),
      width: '8%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => {
        if (record.deletedAt) {
          return <Badge status="default" text="Deleted" />;
        }
        return (
          <Badge
            status={isActive ? 'success' : 'error'}
            text={isActive ? 'Active' : 'Inactive'}
          />
        );
      },
      width: '6%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '12%',
      render: (_, record) => (
        <Space size="middle">
          {!record.deletedAt && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditContract(record)}
              aria-label={`Edit ${record.contractName}`}
            />
          )}

          {record.deletedAt ? (
            <>
              <Popconfirm
                title={`Are you sure you want to restore "${record.contractName}"?`}
                description="This will reactivate the contract and allow new tickets."
                onConfirm={() => handleRestoreContract(record.id, record.contractName)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  loading: restoringId === record.id
                }}
              >
                <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Restore ${record.contractName}`} />
              </Popconfirm>
              <Popconfirm
                title={`Are you sure you want to PERMANENTLY DELETE contract "${record.contractName}"?`}
                description="This action cannot be undone and will fail if tickets are linked."
                onConfirm={() => handleHardDeleteContract(record.id, record.contractName)}
                okText="Yes, Delete Permanently"
                cancelText="No, Keep It"
                okButtonProps={{ danger: true, loading: hardDeletingId === record.id }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Hard Delete ${record.contractName}`}
                />
              </Popconfirm>
            </>
          ) : (
            <>
              {record.isActive ? (
                <Popconfirm
                  title={`Are you sure you want to deactivate "${record.contractName}"?`}
                  description="This will prevent further ticket creation for this contract."
                  onConfirm={() => handleDeactivateContract(record.id, record.contractName)}
                  okText="Yes, deactivate"
                  cancelText="Cancel"
                  okButtonProps={{
                    loading: deactivatingId === record.id,
                    danger: true
                  }}
                >
                  <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.contractName}`} />
                </Popconfirm>
              ) : (
                <Popconfirm
                  title={`Are you sure you want to activate "${record.contractName}"?`}
                  description="This will allow new tickets to be created for this contract."
                  onConfirm={() => handleActivateContract(record.id, record.contractName)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{
                    loading: activatingId === record.id
                  }}
                >
                  <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.contractName}`} />
                </Popconfirm>
              )}
              <Popconfirm
                title={`Are you sure you want to soft delete contract "${record.contractName}"?`}
                description="It will become inactive and can be restored later."
                onConfirm={() => handleSoftDeleteContract(record.id, record.contractName)}
                okText="Yes, Soft Delete"
                cancelText="No"
                okButtonProps={{ danger: true, loading: softDeletingId === record.id }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Soft Delete ${record.contractName}`}
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="contracts-page">
      <div className="contracts-header">
        <h2>Contract Management</h2>
        <div className="contracts-actions">
          <Space size="middle">
            <Search
              placeholder="Search contracts..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => fetchContracts(filterStatus, value, true)}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 150 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchContracts(value, searchText, true);
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status (Incl. Deleted)</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContract}
              size="large"
            >
              Add Contract
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={contracts}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <ContractForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={handleModalSuccess}
        contract={editingContract}
      />
    </div>
  );
};

export default Contracts;